import { findIndex } from "@fluentui/react/lib/Utilities";

import {
  findParentItemByClass,
  idWithSuffix,
  getRibbonTextContainerSelector,
} from "@1js/acui-common";
import type { AppContextType } from "@1js/acui-context";
import { galleryButtonAutomationTypeSuffix } from "@1js/acui-gallery";

import {
  estimateRibbonScalingStepsMLR,
  estimateRibbonScalingStepsSLR,
  scaleRibbonDownWithBuildTimeScalingMLR,
  scaleRibbonDownWithBuildTimeScalingSLR,
} from ".";
import type {
  BuildTimeControlWidths,
  ControlDimensions,
  EstimatedScalingWidthDict,
  RibbonScalingState,
  RibbonScalingStateMLR,
  RibbonScalingStateSLR,
  RibbonScalingStateSLRWithCacheKeys,
  ScaledRibbonGroupSLR,
  VirtualRibbonScalingState,
  VirtualRibbonScalingStateSLR,
} from "..";
import { scaleRibbonDown as scaleRibbonDownExternal, scaleRibbonUp } from "..";
import type { RibbonControlProps } from "../../../Controls";
import type {
  RibbonControlScalingTransformation,
  RibbonGroupScalingTransformation,
  RibbonScalingTransformation,
} from "../../../Scaling";
import {
  ghostFlyoutIdSuffix,
  moveToOverflowScalingClassName,
  overflowIdSuffix,
} from "../../../Scaling";
import type { RibbonMode } from "../../Ribbon/Ribbon.Props";
import { ribbonOverflowSetItemContainerClass } from "../../RibbonControlGroupSLR";

export interface EstimatedRibbonWidth {
  width: number;
  unknownIds: string[];
  totalIdCount: number;
}

/**
 * Scales the ribbon up one step and appends the estimatedWidth of applying that step.
 *
 * @param scalingState The current scaling state for the ribbon. This holds all data necessary to render and transform the ribbon
 * @param estimatedWidths A dictionary of estimated widths when applying each scaling step.
 * @returns The new scaling state after applying the next transformation
 */
export function scaleRibbonUpWithEstimatedWidth(
  scalingState: RibbonScalingStateSLR,
  estimatedWidths: EstimatedScalingWidthDict
): RibbonScalingStateSLR | undefined {
  const newScalingState: RibbonScalingState | undefined =
    scaleRibbonUp(scalingState);
  return newScalingState
    ? {
        ...newScalingState,
        estimatedWidth:
          estimatedWidths[newScalingState.appliedScalingStepCount],
      }
    : undefined;
}

/**
 * Scales the ribbon down one step and appends the estimatedWidth of applying that step.
 *
 * @param scalingState The current scaling state for the ribbon. This holds all data necessary to render and transform the ribbon
 * @param estimatedWidths A dictionary of estimated widths when applying each scaling step.
 * @param isSpanning True if the ribbon is currently spanning across multiple windows
 * @returns The new scaling state after applying the next transformation
 */
export function scaleRibbonDownWithEstimatedWidth(
  scalingState: RibbonScalingStateSLR,
  estimatedWidths: EstimatedScalingWidthDict
): RibbonScalingState | undefined {
  const newScalingState: RibbonScalingState | undefined =
    scaleRibbonDownExternal(scalingState);
  return newScalingState
    ? {
        ...newScalingState,
        estimatedWidth:
          estimatedWidths[newScalingState.appliedScalingStepCount],
      }
    : undefined;
}

export function estimateRibbonScalingSteps(
  scalingState: RibbonScalingState,
  measuredDimension: number,
  mode: RibbonMode,
  appContext: AppContextType,
  draftVirtualScalingState?: VirtualRibbonScalingState
) {
  switch (mode) {
    case "SingleLine":
      return estimateRibbonScalingStepsSLR(
        scalingState as RibbonScalingStateSLRWithCacheKeys,
        measuredDimension,
        appContext,
        draftVirtualScalingState as VirtualRibbonScalingStateSLR
      );
    case "Multiline":
      return estimateRibbonScalingStepsMLR(
        scalingState as RibbonScalingStateMLR,
        measuredDimension,
        appContext
      );
  }
}

/**
 * Returns the width of the given element
 *
 * @param element The element to measure
 * @returns The width of the element in pixels
 */
export function getElementWidth(element: Element | null | undefined) {
  const elementWidth = element?.getBoundingClientRect()?.width;
  if (elementWidth) {
    return Math.ceil(elementWidth);
  }
  return elementWidth ? Math.ceil(elementWidth) : elementWidth;
}

/**
 * Given a control's ID, this function returns the controlGroup object that contains it
 *
 * @param scalingState The current scaling state for the ribbon. This holds all data necessary to render and transform the ribbon
 * @param controlId A control within the desired controlGroup
 * @returns The controlGroup that contains {controlId}
 */
export function getControlGroupFromControlId(
  scalingState: RibbonScalingStateSLR,
  controlId: string
) {
  const controlGroupIndex = findIndex(
    scalingState.scaledRibbonGroups,
    (group: ScaledRibbonGroupSLR) =>
      group.topLevelControls.some((control) => control.id === controlId) ||
      group.overflowControls.some((control) => control.id === controlId)
  );
  return controlGroupIndex >= 0
    ? scalingState.scaledRibbonGroups[controlGroupIndex]
    : undefined;
}

/**
 * Given a groups's ID, this function returns the controlGroup object that matches it
 *
 * @param scalingState The current scaling state for the ribbon. This holds all data necessary to render and transform the ribbon
 * @param groupId A ID of the desired controlGroup
 * @returns The controlGroup that matches {groupId}
 */
export function getControlGroupFromGroupId(
  scalingState: RibbonScalingStateSLR,
  groupId: string
) {
  const controlGroupIndex = findIndex(
    scalingState.scaledRibbonGroups,
    (group: ScaledRibbonGroupSLR) => group.id === groupId
  );
  return controlGroupIndex >= 0
    ? scalingState.scaledRibbonGroups[controlGroupIndex]
    : undefined;
}

/**
 * Given a scalingStep, this function returns the controlGroup object that matches it
 * @param scalingState The current scaling state for the ribbon. This holds all data necessary to render and transform the ribbon
 * @param scalingStep A scaling step to find the group for. If an array is passed in, return the first matching group
 * @returns
 */
export function getControlGroupFromScalingStep(
  scalingState: RibbonScalingStateSLR,
  scalingStep:
    | RibbonControlScalingTransformation
    | RibbonGroupScalingTransformation
): ScaledRibbonGroupSLR | undefined {
  let id = (scalingStep as RibbonControlScalingTransformation).controlId;
  let getControlGroup = getControlGroupFromControlId;
  if (!id) {
    id = (scalingStep as RibbonGroupScalingTransformation).groupId;
    getControlGroup = getControlGroupFromGroupId;
  }

  return getControlGroup(scalingState, id);
}

/**
 * Given a control's ID, this function returns the props for that control
 *
 * @param scalingState The current scaling state for the ribbon. This holds all data necessary to render and transform the ribbon
 * @param controlId A ID of the desired control
 * @returns The props for the given control
 */
export function getControlProps(
  controlGroup: ScaledRibbonGroupSLR,
  controlId: string
): RibbonControlProps | undefined {
  const controlIndex = findIndex(
    controlGroup.topLevelControls,
    (control: RibbonControlProps) => control.id === controlId
  );
  return controlIndex >= 0
    ? controlGroup.topLevelControls[controlIndex]
    : undefined;
}

/**
 * Given a control's ID, this function returns the props for the control that is rendered directly after it
 *
 * @param scalingState The current scaling state for the ribbon. This holds all data necessary to render and transform the ribbon
 * @param controlId A ID of the desired control
 * @returns The props for the adjacent control
 */
export function getAdjacentControlProps(
  controlGroup: ScaledRibbonGroupSLR,
  controlId: string
): RibbonControlProps | undefined {
  const controlIndex = findIndex(
    controlGroup.topLevelControls,
    (control: RibbonControlProps) => control.id === controlId
  );
  if (
    controlIndex >= 0 &&
    controlIndex < controlGroup.topLevelControls.length - 1
  ) {
    return controlGroup.topLevelControls[controlIndex + 1];
  }
  return undefined;
}

/**
 * Given a scaling step, this function finds the DOM element that corresponds to the targeted ribbon control
 *
 * @param scalingStep The current scaling step that is being applied
 * @returns The Element for the targeted control
 */
export function getCoreRibbonElement(
  scalingStep: RibbonScalingTransformation
): Element | null {
  switch (scalingStep.type) {
    case "DropLabel":
    case "MoveToOverflow":
    case "DropPreviewItemFromGallery":
    case "GalleryToFlyout":
      return getRibbonElementContainer(
        getCoreRibbonElementById(scalingStep.controlId)
      );
  }
  return null;
}

/**
 * Given an control's ID, this util returns the core DOM element associated with that control
 * @param id The ID of the control you want to find
 */
export function getCoreRibbonElementById(id: string): HTMLElement | null {
  return document.querySelector(getCoreRibbonElementSelector(id, undefined));
}

export function getControlDimensionsForRibbonModeById(
  id: string,
  ribbonMode: RibbonMode,
  explicitRoot?: HTMLElement
): ControlDimensions | undefined {
  const rootElement =
    explicitRoot ??
    (ribbonMode === "Multiline"
      ? getCoreRibbonElementById(id)
      : getRibbonElementContainer(getCoreRibbonElementById(id)));

  if (!rootElement) {
    return {
      componentRoot: 0,
      textContainer: undefined,
      ghostFlyout: undefined,
      ghostFlyoutTextContainer: undefined,
      previewGalleryItems: undefined,
    };
  }

  const textContainer = getRibbonElementTextContainer(rootElement);
  const previewGalleryItems = getRibbonPreviewGalleryItems(rootElement);
  const ghostFlyout = getPreviewGalleryGhostFlyout(
    rootElement.parentElement,
    id
  );
  const ghostFlyoutTextContainer = getRibbonElementTextContainer(ghostFlyout);

  return {
    componentRoot: getElementWidth(rootElement) || 0,
    textContainer: textContainer ? getElementWidth(textContainer) : undefined,
    ghostFlyout: ghostFlyout ? getElementWidth(ghostFlyout) : undefined,
    ghostFlyoutTextContainer: ghostFlyoutTextContainer
      ? getElementWidth(ghostFlyoutTextContainer)
      : undefined,
    previewGalleryItems:
      previewGalleryItems.length > 0
        ? previewGalleryItems.map((item: Element) => getElementWidth(item) || 0)
        : undefined,
  };
}

/**
 * Gets a selector to query for an element given an id, suffix, and UILocation
 * @param id Id of the element
 * @param suffix Optional, suffix that is used for the data-unique-id (like 'overflow' or 'ghostFlyout')
 * @param location Optional, UILocation of the element (like UILocation.Ribbon or UILocation.Overflow), default UILocation.Ribbon
 * @returns Returns a correctly escaped selector to query for the element, by data-unique-id
 */
export function getCoreRibbonElementSelector(
  id: string,
  suffix: string = ""
): string {
  // escape single quotes within the ID to prevent an invalid query from throwing an exception
  const controlIdToQuery = id.replace(/['\\]/g, "\\'");
  return `#${idWithSuffix(controlIdToQuery, suffix)}`;
}

/**
 * Gets a selector for a ghostFlyout (e.g. a flyout created for the galleryToFlyout scaling step)
 * @param id Id of the element
 * @returns Returns a correctly escaped selector to query for the element, by data-unique-id
 */
export function getCoreRibbonElementForGalleriesConvertedToFlyoutAnchorsSelector(
  id: string
): string {
  return getCoreRibbonElementSelector(id, ghostFlyoutIdSuffix);
}

/**
 * Gets a selector for an overflow element with the given id
 * @param id Id of the element
 * @returns Returns a correctly escaped selector to query for the element, by data-unique-id
 */
export function getCoreRibbonElementForOverflowSelector(id: string): string {
  return getCoreRibbonElementSelector(id, overflowIdSuffix);
}

/**
 * Gets a className for the moveToOverflow scaling step at the given index
 * @param scalingIndex The scaling index that corresponds to the moveToOverflow scaling step
 * @returns Returns the className for the scaling step
 */
export function getMoveToOverflowScalingClassNameForScalingIndex(
  scalingIndex: number
) {
  return `${moveToOverflowScalingClassName}${scalingIndex}`;
}

/**
 * Gets a selector for the moveToOverflow scaling step
 * @param topLevelScalingIndex The top level scaling index that corresponds to the moveToOverflow scaling step
 * @returns Returns a selector to query for the element
 */
export function getMoveToOverflowSelector(topLevelScalingIndex: number) {
  return `.${getMoveToOverflowScalingClassNameForScalingIndex(
    topLevelScalingIndex
  )}`;
}

/**
 * Returns an element's parent overflowSetItem if it exists in the DOM.
 * @param element The core element to search from
 */
export function getRibbonElementContainer(
  element: HTMLElement | null
): Element | null {
  return findParentItemByClass(element, ribbonOverflowSetItemContainerClass);
}

/**
 * Given a DOM element associated with a control, this util finds and returns the textContainer element that holds the label
 * @param componentRoot The core element to search from
 */
export function getRibbonElementTextContainer(
  componentRoot: Element | null | undefined
): Element | null | undefined {
  return componentRoot?.querySelector(
    getRibbonTextContainerSelector(true /**useOptimizedSelector*/)
  );
}

/**
 * Given a DOM element associated with a control, this util finds and returns the list of preview gallery items within it
 * @param componentRoot The core element to search from
 */
export function getRibbonPreviewGalleryItems(
  componentRoot: Element | null | undefined
): Element[] {
  const previewGalleryItems: NodeListOf<Element> | null | undefined =
    componentRoot?.querySelectorAll(
      `[data-automation-type$='${galleryButtonAutomationTypeSuffix}']`
    );

  if (!previewGalleryItems) {
    return [];
  }

  const previewGalleryItemsList: Element[] = [];

  for (let i = 0; i < previewGalleryItems.length; i += 1) {
    if (hasDisplayNone(previewGalleryItems[i])) {
      continue;
    }
    previewGalleryItemsList.push(previewGalleryItems[i]);
  }

  return previewGalleryItemsList;
}

const hasDisplayNone = (elem: Element) => {
  return (elem as any)?.computedStyleMap
    ? (elem as any).computedStyleMap().get("display").value === "none"
    : getComputedStyle?.(elem).display === "none" || false;
};

/**
 * Given a DOM element associated with a control, this util finds and returns the preview gallery ghost flyout if it exists
 * @param componentRoot The core element to search from
 * @param id The id of the component
 */
export function getPreviewGalleryGhostFlyout(
  componentRoot: Element | null | undefined,
  id: string
): Element | null | undefined {
  return componentRoot?.querySelector(
    getCoreRibbonElementForGalleriesConvertedToFlyoutAnchorsSelector(id)
  );
}

/**
 * Determines the scalingState to be rendered given the buildTimeControlWidths
 * @param scalingState The scaling state outlining the shape of the current ribbon
 * @param knownWidths The dictionary of component widths determined at build-time
 * @param reactMLRIsEnabled True if the React MLR is currently enabled in this session
 * @param reservedSpace How much space is reserved for non-Ribbon elements
 * @param onWarning The callback used to report any warnings that are hit
 * @param ribbonMode The current ribbonMode that is being rendered
 */
export function scaleRibbonDownWithBuildTimeScaling(
  scalingState: RibbonScalingState,
  knownWidths: BuildTimeControlWidths,
  reactMLRIsEnabled: boolean,
  reservedSpace: number,
  onWarning: ((message: string) => void) | undefined,
  ribbonMode: RibbonMode,
  appContext: AppContextType
) {
  switch (ribbonMode) {
    case "SingleLine":
      return scaleRibbonDownWithBuildTimeScalingSLR(
        scalingState as RibbonScalingStateSLRWithCacheKeys,
        knownWidths,
        reactMLRIsEnabled,
        reservedSpace,
        onWarning,
        appContext
      );
    case "Multiline":
      return scaleRibbonDownWithBuildTimeScalingMLR(
        scalingState as RibbonScalingStateMLR,
        knownWidths,
        reservedSpace,
        onWarning,
        appContext
      );
  }
}
